import { Fragment, type ReactNode } from 'react';

/**
 * Renders an event's "About this event" text (task #5) SAFELY - never `dangerouslySetInnerHTML`
 * on unsanitised content. `text` is assumed plain text that may contain lightweight markdown-ish
 * structure (`#`/`##`/`###` headings, `-`/`*` bullet lines, blank-line-separated paragraphs) per
 * the brief. Any literal `<tag>` characters a tenant types are rendered as plain visible text by
 * React's normal escaping (`{child}` is always text, never parsed as HTML) - there is no code path
 * here that can turn buyer- or tenant-authored text into executed markup.
 */
export function AboutContent({ text }: { text: string }) {
  const blocks = parseLightweightMarkdown(text);
  if (blocks.length === 0) return null;
  return <div className="space-y-2 text-sm text-slate-700">{blocks}</div>;
}

function parseLightweightMarkdown(text: string): ReactNode[] {
  const lines = text.split(/\r?\n/);
  const nodes: ReactNode[] = [];
  let listBuffer: string[] = [];
  let paraBuffer: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const items = listBuffer;
    listBuffer = [];
    nodes.push(
      <ul key={`ul-${key++}`} className="list-disc pl-5 space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>,
    );
  };

  const flushPara = () => {
    if (paraBuffer.length === 0) return;
    const linesInPara = paraBuffer;
    paraBuffer = [];
    nodes.push(
      <p key={`p-${key++}`}>
        {linesInPara.map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </p>,
    );
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = /^(#{1,3})\s+(.+)$/.exec(line);
    const bulletMatch = /^[-*]\s+(.+)$/.exec(line);

    if (headingMatch) {
      flushList();
      flushPara();
      const level = headingMatch[1]!.length;
      const headingText = headingMatch[2]!;
      const headingClass = 'font-heading font-semibold text-slate-800 mt-3 first:mt-0';
      if (level === 1) {
        nodes.push(
          <h4 key={`h-${key++}`} className={`${headingClass} text-base`}>
            {headingText}
          </h4>,
        );
      } else if (level === 2) {
        nodes.push(
          <h5 key={`h-${key++}`} className={`${headingClass} text-sm`}>
            {headingText}
          </h5>,
        );
      } else {
        nodes.push(
          <h6 key={`h-${key++}`} className={`${headingClass} text-sm`}>
            {headingText}
          </h6>,
        );
      }
    } else if (bulletMatch) {
      flushPara();
      listBuffer.push(bulletMatch[1]!);
    } else if (line === '') {
      flushList();
      flushPara();
    } else {
      flushList();
      paraBuffer.push(line);
    }
  }
  flushList();
  flushPara();
  return nodes;
}
